import React from "react";
import HorizontalBarChart from "@charty/horizontal-bar-chart";
import { Range } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { ComparisonPeriod } from "@radarly/models/cards/period";
import { DatavizHorizontalBarChart } from "@radarly/models/dataviz";
import Empty from "../../empty";
import Tooltip from "../../tooltip/horizontal-bar-chart";
import { getTooltipCount, useHorizontalBarChart } from "./model";

type HorizontalBarChartProps = React.ComponentProps<typeof HorizontalBarChart>;

type SharedHorizontalBarChartProps = Pick<
  HorizontalBarChartProps,
  "gauges" | "onClickGauge" | "isFullscreen" | "isPNG"
> &
  Partial<
    Pick<
      HorizontalBarChartProps,
      "showIndex" | "showTrackWhenSuperNarrow" | "aside" | "sort" | "order"
    >
  > & {
    dataviz: DatavizHorizontalBarChart;
    range: Range;
    metric: Metric;
    className?: string;
    total?: number;
    disableAnimation?: boolean;
    comparisonPeriod?: ComparisonPeriod;
    isHideable?: boolean;
    closeFullscreen?: () => void;
  };

export default function SharedHorizontalBarChart({
  gauges,
  dataviz,
  className,
  onClickGauge,
  range,
  metric,
  sort: initialSort,
  order: initialOrder,
  total,
  comparisonPeriod,
  isFullscreen,
  isHideable,
  closeFullscreen,
  ...props
}: SharedHorizontalBarChartProps) {
  const {
    isEmpty,
    locale,
    customColor,
    limit,
    displayNullValues,
    isNarrow,
    mainUnit,
    secondaryUnit,
    showAvatar,
    order,
    sort,
  } = useHorizontalBarChart(
    dataviz,
    metric,
    gauges,
    initialSort,
    initialOrder,
    comparisonPeriod
  );
  return (
    <Empty empty={isEmpty}>
      <HorizontalBarChart
        gauges={gauges}
        className={className}
        locale={locale}
        customColor={customColor}
        limit={limit}
        displayNullValues={displayNullValues}
        isNarrow={isNarrow}
        showAvatar={showAvatar}
        onClickGauge={onClickGauge}
        mainUnit={mainUnit}
        secondaryValue={secondaryUnit}
        sort={sort}
        order={order}
        total={total}
        isFullscreen={isFullscreen}
        isHideable={isHideable}
        tooltip={(serie) => (
          <Tooltip
            count={serie.count}
            color={dataviz.color || serie.color}
            label={serie.label}
            metric={metric}
            locale={locale}
            total={total}
            content={getTooltipCount(mainUnit, serie.count)}
            mainUnit={mainUnit}
            secondaryUnit={secondaryUnit}
            comparisonPeriod={comparisonPeriod}
            evolution={serie.evolution}
            isHidden={serie.isHidden}
            isHideable={isHideable}
          />
        )}
        {...props}
      />
    </Empty>
  );
}
